export interface NoteMention {
	userId: string
	name: string
}

export type NoteSegment =
	| { type: 'text'; text: string }
	| { type: 'mention'; userId: string; name: string }

export interface MentionQuery {
	start: number
	query: string
}

const MENTION_TOKEN_SOURCE = '@\\[([^\\[\\]]+)\\]\\(([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\\)'
const MENTION_QUERY = /(?:^|\s)@([^@\n]{0,40})$/
const WORD_CHARACTER = /[\p{L}\p{N}]/u

function mentionTokens(): RegExp {
	return new RegExp(MENTION_TOKEN_SOURCE, 'gi')
}

export function parseNoteSegments(content: string): NoteSegment[] {
	const segments: NoteSegment[] = []
	let cursor = 0

	for (const match of content.matchAll(mentionTokens())) {
		const [token, name = '', userId = ''] = match
		const index = match.index ?? 0

		if (index > cursor) {
			segments.push({ type: 'text', text: content.slice(cursor, index) })
		}
		segments.push({ type: 'mention', userId, name })
		cursor = index + token.length
	}

	if (cursor < content.length) {
		segments.push({ type: 'text', text: content.slice(cursor) })
	}

	return segments
}

export function mentionedUserIds(content: string): string[] {
	const userIds = parseNoteSegments(content).flatMap((segment) =>
		segment.type === 'mention' ? [segment.userId.toLowerCase()] : []
	)

	return [...new Set(userIds)]
}

export function deserializeMentions(content: string): { text: string; mentions: NoteMention[] } {
	const mentions: NoteMention[] = []
	const text = content.replace(mentionTokens(), (_token: string, name: string, userId: string) => {
		mentions.push({ userId, name })
		return `@${name}`
	})

	return { text, mentions }
}

export function sanitizeMentionName(name: string): string {
	return name.replace(/[[\]()]/g, '').replace(/\s+/g, ' ').trim()
}

function findMentionIndex(text: string, name: string, from: number): number {
	const needle = `@${name}`
	let index = text.indexOf(needle, from)

	while (index !== -1) {
		if (!WORD_CHARACTER.test(text.charAt(index + needle.length))) {
			return index
		}
		index = text.indexOf(needle, index + 1)
	}

	return -1
}

export function serializeMentions(text: string, mentions: NoteMention[]): string {
	const remaining = mentions.filter((mention) => mention.name !== '')
	let result = ''
	let cursor = 0

	while (remaining.length > 0) {
		let best: { index: number; position: number; length: number } | null = null

		for (const [position, mention] of remaining.entries()) {
			const index = findMentionIndex(text, mention.name, cursor)
			if (index === -1) {
				continue
			}
			if (best === null || index < best.index || (index === best.index && mention.name.length > best.length)) {
				best = { index, position, length: mention.name.length }
			}
		}

		if (best === null) {
			break
		}

		const [mention] = remaining.splice(best.position, 1)
		if (!mention) {
			break
		}

		result += `${text.slice(cursor, best.index)}@[${mention.name}](${mention.userId})`
		cursor = best.index + mention.name.length + 1
	}

	return result + text.slice(cursor)
}

export function findMentionQuery(text: string, caret: number): MentionQuery | null {
	const beforeCaret = text.slice(0, caret)
	const match = MENTION_QUERY.exec(beforeCaret)
	if (!match) {
		return null
	}

	const query = match[1] ?? ''
	if (query.startsWith(' ')) {
		return null
	}

	return { start: beforeCaret.length - query.length - 1, query }
}
